import React from 'react'
import { IconX } from '@tabler/icons-react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'

import { Loader } from '@/components/loaders/Loader'
import { SKIP_MODIFIER } from '@/utils/constants'
import { Button } from '@/components/buttons/Button'

import { SkipButton } from './components/SkipButton'
import { PlayPauseButton } from './components/PlayPauseButton'
import { PlaybackRateDropdown } from './components/PlaybackRateDropdown'
import { AudioTrackTime } from './components/AudioTrackTime'
import { AudioTrackProgress } from './components/AudioTrackProgress'
import { VolumeSlider } from './components/VolumeSlider'
import { useAudioPlayer } from './AudioPlayerContext'
import { useTranscript } from '../transcript/TranscriptContext'
import { AudioShareButton } from './components/AudioShareButton'
import { SentimentGraph } from './components/SentimentGraph'

import './AudioPlayer.scss'

export const AudioPlayer = ({
  callId,
  loading,
  autoFocus = false,
  isPlaying,
  progress,
  duration,
  playbackRate,
  volume,
  audioUrl,
  isDisabled,
  disabledTooltip,
  handleScrub,
  handleBackSkip,
  handleForwardSkip,
  handleVolumeChange,
  handlePlaybackRateChange,
  handleCloseDrawer,
  togglePlayState,
  hideCloseButton,
  hideShareButton,
  isTranscriptAvailable = false,
  skipSilence,
  handleSkipSilenceChange,
  showTranscriptButton,
  setProgress,
  metadata,
  useMinimalUi = false,
}) => {
  const { audioRef } = useAudioPlayer()
  const { transcriptToDisplay, isTranscriptVisible, handleToggleTranscriptVisibility } =
    useTranscript()
  const shouldShowTranscriptButton =
    showTranscriptButton && isTranscriptAvailable && !isEmpty(transcriptToDisplay)

  return (
    <>
      <audio src={audioUrl} ref={audioRef} />
      <div
        className={classNames('audio-player', { 'audio-player--minimal': useMinimalUi })}
        data-testid="audio-player"
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="audio-player-controls" data-testid="audio-controls">
              <PlayPauseButton
                isPlaying={isPlaying}
                togglePlayState={togglePlayState}
                isDisabled={isDisabled}
                disabledTooltip={disabledTooltip}
                autoFocus={autoFocus}
              />
              {!useMinimalUi && (
                <>
                  <div className="flex-align-center small-gap">
                    <SkipButton
                      modifier={SKIP_MODIFIER}
                      handleSkip={handleBackSkip}
                      isDisabled={isDisabled}
                    />
                    <SkipButton
                      modifier={SKIP_MODIFIER}
                      handleSkip={handleForwardSkip}
                      isForwardSkip
                      isDisabled={isDisabled}
                    />
                  </div>
                  <VolumeSlider
                    volume={volume}
                    handleVolumeChange={handleVolumeChange}
                    isDisabled={isDisabled}
                  />
                </>
              )}
            </div>
            {!useMinimalUi && (
              <>
                <div className="audio-player-options">
                  <PlaybackRateDropdown
                    isDisabled={isDisabled}
                    handlePlaybackRateChange={handlePlaybackRateChange}
                    playbackRate={playbackRate}
                    skipSilence={skipSilence}
                    handleSkipSilenceChange={handleSkipSilenceChange}
                    isTranscriptAvailable={isTranscriptAvailable}
                  />
                  {!hideShareButton && (
                    <AudioShareButton progress={progress} duration={duration} callId={callId} />
                  )}
                  {shouldShowTranscriptButton && (
                    <Button small onClick={handleToggleTranscriptVisibility}>
                      {isTranscriptVisible ? 'Hide Transcript' : 'Show Transcript'}
                    </Button>
                  )}
                  {!hideCloseButton && (
                    <Button
                      square
                      small
                      basic
                      aria-label="Close Player"
                      onClick={handleCloseDrawer}
                    >
                      <IconX />
                    </Button>
                  )}
                </div>
                <div className="audio-player-track" data-testid="audio-progress-track">
                  <AudioTrackTime duration={duration} progress={progress} />
                  <AudioTrackProgress
                    isDisabled={isDisabled}
                    duration={duration}
                    progress={progress}
                    handleScrub={handleScrub}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
      {!useMinimalUi && (
        <SentimentGraph
          duration={duration}
          progress={progress}
          setProgress={setProgress}
          callId={callId}
          metadata={metadata}
          organizationId={metadata?.organization_id}
        />
      )}
    </>
  )
}
